import { createAction, PayloadAction } from '@reduxjs/toolkit';
import React from 'react';
import { all, call, delay, put, select, take, takeLatest, takeLeading } from 'redux-saga/effects';

import DiseaseApi from 'api/DiseaseApi';
import RiskFeedApi from 'api/RiskFeedApi';
import LogApi from 'api/LogApi';
import { openNotificationPngDownload } from 'app-redux/notification.const';
import { hideNotification, showNotification } from 'app-redux/notification.saga';
import * as dto from 'client/dto';
import theme from 'theme';
import { DefaultTimeframeStartDateType } from 'utils/constants';
import { getMomentAgo } from 'utils/dateTimeHelpers';
import { runHtml2Canvas, ToZipScreenshot, zipAndSaveScreenshots } from 'utils/exportToPngHelpers';

import { fetchBepiForLocation } from 'bepi/bepi.saga';
import diseasesSelectors from 'diseases/diseases.selectors';
import locationsSelectors from 'locations/locations.selectors';

import RiskOverviewCardActivityLandscape from './containers/exports/landscape/RiskOverviewCardActivityLandscape';
import RiskOverviewCardBasicLandscape from './containers/exports/landscape/RiskOverviewCardBasicLandscape';
import RiskOverviewCardBepiLandscape from './containers/exports/landscape/RiskOverviewCardBepiLandscape';
import RiskOverviewCardDisruptionLandscape from './containers/exports/landscape/RiskOverviewCardDisruptionLandscape';
import RiskOverviewCardActivityPortrait from './containers/exports/portrait/RiskOverviewCardActivityPortrait';
import RiskOverviewCardDisruptionPortrait from './containers/exports/portrait/RiskOverviewCardDisruptionPortrait';
import riskOverviewSelectors from './riskOverview.selectors';
import {
  clearRiskOverview,
  finishExportingPngs,
  loadImportationSourcesError,
  loadImportationSourcesStart,
  loadImportationSourcesSuccess,
  loadRiskFeedIncidenceError,
  loadRiskFeedIncidenceStart,
  loadRiskFeedIncidenceSuccess,
  loadRiskOverviewError,
  loadRiskOverviewStart,
  loadRiskOverviewSuccess,
  startExportingPngs,
} from './riskOverview.slice';
import { RiskOverviewPageSize } from './riskOverviewHelper';

interface FetchDiseaseRiskImportationSourcesPayload {
  diseaseActivityId: number;
  locationId: number;
}

interface FetchIncidenceDataPayload {
  diseaseId: number;
  locationId: number;
  startDate: string;
}

export type ExportRiskOverviewPayload = {
  orientation: 'portrait' | 'landscape';
  diseaseId: number;
  locationId: number;
  activityId: number;
  startDate: string;
  pngHeader: string;
  completeCallback?: () => void;
};

export type FetchRiskOverviewPayload = { paginateQuery: boolean; debounceDelay?: number };

export const initialRiskOverviewFetch = createAction('riskOverview/initialFetch');
export const fetchRiskOverview = createAction<FetchRiskOverviewPayload>('riskOverview/fetch');
export const fetchDiseaseRiskImportationSources =
  createAction<FetchDiseaseRiskImportationSourcesPayload>(
    'riskOverview/fetchDiseaseRiskImportationSources'
  );
export const fetchIncidenceData = createAction<FetchIncidenceDataPayload>(
  'riskOverview/fetchIncidenceData'
);
export const exportRiskPngs = createAction<ExportRiskOverviewPayload>(
  'riskOverview/exportRiskPngs'
);

function* fetchRiskIncidence(data: FetchIncidenceDataPayload) {
  const res = yield call(RiskFeedApi.getRiskIncidence, data);

  yield put(
    loadRiskFeedIncidenceSuccess({
      ...data,
      riskFeedIncidence: res,
    })
  );
}

function* fetchImportationRiskSources(data: FetchDiseaseRiskImportationSourcesPayload) {
  const response = yield call(DiseaseApi.getDiseaseRiskImportationSources, data);
  yield put(
    loadImportationSourcesSuccess({
      diseaseActivityId: data.diseaseActivityId,
      locationId: data.locationId,
      sources: response,
    })
  );
}

export function* fetchRiskOverviewSaga(action: PayloadAction<FetchRiskOverviewPayload>) {
  try {
    if (action.payload.debounceDelay > 0) {
      yield delay(action.payload.debounceDelay);
    }

    const filteredDiseaseIds = yield select(riskOverviewSelectors.getFilteredDiseaseIds);
    const diseaseIds = yield select(diseasesSelectors.getAllDiseaseIds);
    const lastEntry = yield select(riskOverviewSelectors.lastRiskFeedPosition);
    const filteredLocationIds = yield select(riskOverviewSelectors.getFilteredLocationIds);
    const locationIds = yield select(locationsSelectors.getAllLocationIds);
    const sortBy = yield select(riskOverviewSelectors.getRiskFeedSortType);

    const requestModel: dto.RiskFeedRequestModel = {
      activityStartDate: getMomentAgo(DefaultTimeframeStartDateType).toISOString(),
      diseaseIds: filteredDiseaseIds?.length ? filteredDiseaseIds : diseaseIds,
      lastEntry: action.payload.paginateQuery ? lastEntry : null,
      locationIds: filteredLocationIds?.length ? filteredLocationIds : locationIds,
      pageSize: RiskOverviewPageSize,
      sortBy,
    };

    if (!action.payload.paginateQuery) {
      yield put(clearRiskOverview());
    }
    yield put(loadRiskOverviewStart());
    const response = yield call(RiskFeedApi.getRiskFeed, requestModel);
    yield put(loadRiskOverviewSuccess(response));
  } catch (e) {
    yield put(loadRiskOverviewError());
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
  }
}

export function* fetchDiseaseRiskImportationSourcesSaga(
  action: PayloadAction<FetchDiseaseRiskImportationSourcesPayload>
) {
  try {
    yield put(
      loadImportationSourcesStart({
        diseaseActivityId: action.payload.diseaseActivityId,
        locationId: action.payload.locationId,
      })
    );
    yield fetchImportationRiskSources(action.payload);
  } catch (e) {
    yield put(
      loadImportationSourcesError({
        diseaseActivityId: action.payload.diseaseActivityId,
        locationId: action.payload.locationId,
      })
    );
    LogApi.sendLog('error', `Message: ${e?.message}, Stack: ${e?.stack}`);
    console.error(e);
  }
}

export function* fetchIncidenceDataSaga(action: PayloadAction<FetchIncidenceDataPayload>) {
  try {
    yield put(loadRiskFeedIncidenceStart(action.payload));
    yield call(fetchRiskIncidence, action.payload);
  } catch (e) {
    yield put(
      loadRiskFeedIncidenceError({
        diseaseId: action.payload.diseaseId,
        locationId: action.payload.locationId,
      })
    );
  }
}

export function* initialRiskOverviewFetchSaga() {
  yield put(loadRiskOverviewStart());

  const diseaseIds = yield select(diseasesSelectors.getAllDiseaseIds);
  const locationIds = yield select(locationsSelectors.getAllLocationIds);
  const takeActions: string[] = [];

  if (!diseaseIds?.length) {
    takeActions.push('FETCH_DISEASES_FINISH');
  }
  if (!locationIds?.length) {
    takeActions.push('FETCH_LOCATIONS_FINISH');
  }

  if (!takeActions.length) {
    yield put(fetchRiskOverview({ paginateQuery: false }));
  } else {
    yield all(takeActions.map(x => take(x)));
    yield put(fetchRiskOverview({ paginateQuery: false }));
  }
}

const rearrangeActivityDisclaimerForPNG = (doc: Document) => {
  // Moving the disclosure to the bottom of the activity section
  const disclosure = doc.querySelector('#case-incidence-disclosure');
  if (disclosure != null) {
    disclosure.setAttribute('style', 'margin-top: 8px;');
    disclosure
      .querySelector('.disclosure-text')
      .setAttribute('style', `color: ${theme.colors.stone50}`);
    doc.querySelector('#roc-activty-last-checked').setAttribute('style', 'margin-top: 72px;');
    doc.querySelector('#roc-activty-last-checked').appendChild(disclosure);
  }
};

export function* exportRiskPngsSaga(action: PayloadAction<ExportRiskOverviewPayload>) {
  const { diseaseId, locationId, activityId, orientation, startDate, pngHeader, completeCallback } =
    action.payload;
  yield put(openNotificationPngDownload);
  yield put(startExportingPngs());

  try {
    yield call(fetchRiskIncidence, {
      diseaseId: diseaseId,
      locationId: locationId,
      startDate: startDate,
    });

    yield call(fetchImportationRiskSources, {
      diseaseActivityId: activityId,
      locationId: locationId,
    });

    yield put(fetchBepiForLocation(locationId));
    yield take('LOAD_BEPI_SUCCESS');

    const filePrefix = pngHeader.replaceAll(' ', '-');

    let toZipFiles: ToZipScreenshot[] = [];
    let zipFileName = filePrefix;

    if (orientation === 'landscape') {
      const overviewCanvas = yield runHtml2Canvas(
        <RiskOverviewCardBasicLandscape
          activityId={activityId}
          locationId={locationId}
          header={pngHeader}
        />,
        {},
        1,
        1920
      );
      const activityCanvas = yield runHtml2Canvas(
        <RiskOverviewCardActivityLandscape
          header={pngHeader}
          activityId={activityId}
          locationId={locationId}
        />,
        { onclone: doc => rearrangeActivityDisclaimerForPNG(doc) },
        1,
        1920
      );
      const disruptionCanvas = yield runHtml2Canvas(
        <RiskOverviewCardDisruptionLandscape
          header={pngHeader}
          activityId={activityId}
          locationId={locationId}
        />,
        {},
        1,
        1920
      );
      const bepiCanvas = yield runHtml2Canvas(
        <RiskOverviewCardBepiLandscape header={pngHeader} locationId={locationId} />,
        {},
        1,
        1920
      );

      zipFileName = `${zipFileName}-presentation.zip`;
      toZipFiles = [
        { fileName: `${filePrefix}-overview-presentation.png`, canvas: overviewCanvas },
        { fileName: `${filePrefix}-activity-presentation.png`, canvas: activityCanvas },
        { fileName: `${filePrefix}-disruption-presentation.png`, canvas: disruptionCanvas },
        { fileName: `${filePrefix}-preparedness-presentation.png`, canvas: bepiCanvas },
      ];
    } else {
      const activityCanvas = yield runHtml2Canvas(
        <RiskOverviewCardActivityPortrait
          header={pngHeader}
          activityId={activityId}
          locationId={locationId}
        />,
        { onclone: doc => rearrangeActivityDisclaimerForPNG(doc) }
      );
      const disruptionCanvas = yield runHtml2Canvas(
        <RiskOverviewCardDisruptionPortrait
          header={pngHeader}
          activityId={activityId}
          locationId={locationId}
        />
      );

      zipFileName = `${zipFileName}-report.zip`;
      toZipFiles = [
        { fileName: `${filePrefix}-activity-report.png`, canvas: activityCanvas },
        { fileName: `${filePrefix}-disruption-report.png`, canvas: disruptionCanvas },
      ];
    }

    zipAndSaveScreenshots(toZipFiles, zipFileName);

    yield put(finishExportingPngs());
    yield put(hideNotification());

    if (completeCallback != null) completeCallback();
  } catch (e) {
    yield put(finishExportingPngs());
    yield put(
      showNotification({
        type: 'SHOW_ERROR_NOTIFICATION',
        payload: 'There was a problem with your download. Please try again.',
      })
    );
  }
}

function* riskOverviewSaga() {
  yield takeLatest(fetchRiskOverview, fetchRiskOverviewSaga);
  yield takeLatest(fetchDiseaseRiskImportationSources, fetchDiseaseRiskImportationSourcesSaga);
  yield takeLatest(fetchIncidenceData, fetchIncidenceDataSaga);
  yield takeLatest(exportRiskPngs, exportRiskPngsSaga);
  yield takeLeading(initialRiskOverviewFetch, initialRiskOverviewFetchSaga);
}

export default riskOverviewSaga;
